import type { AttackPlan } from "@empire/core-sim";
import { z } from "zod";
import {
  AttackResponse,
  IntelResponse,
  ReplayResponse,
  ReportsResponse,
  TargetsResponse,
  type DesyncReport,
} from "@empire/shared";
import { request } from "./client.js";

/**
 * Attacking somebody, and reading what happened (E3, S5-4 from the client side).
 *
 * Every response is parsed by the schema the server validates against, so a
 * contract change fails here rather than three screens later with a board that
 * is subtly wrong.
 */

export function fetchTargets(token: string): Promise<TargetsResponse> {
  return request("/api/v1/targets", TargetsResponse, { token });
}

export function launchAttack(
  token: string,
  body: {
    fromVillageId: string;
    toVillageId: string;
    // core-sim's own plan type, which is readonly. The wire schema is the
    // mutable mirror of it; going out we serialise, so readonly is fine and
    // keeping the sim's type here means no second definition to drift.
    plan: AttackPlan;
    idempotencyKey: string;
  }
): Promise<AttackResponse> {
  return request("/api/v1/attacks", AttackResponse, { method: "POST", token, body });
}

/** What your own past attacks taught you about one village (S3-2). */
export function fetchIntel(token: string, villageId: string): Promise<IntelResponse> {
  return request(`/api/v1/villages/${villageId}/intel`, IntelResponse, { token });
}

export function fetchReports(token: string, before?: string): Promise<ReportsResponse> {
  const query = before ? `?before=${encodeURIComponent(before)}` : "";
  return request(`/api/v1/reports${query}`, ReportsResponse, { token });
}

export function fetchReplay(token: string, battleId: string): Promise<ReplayResponse> {
  return request(`/api/v1/battles/${battleId}/replay`, ReplayResponse, { token });
}

/**
 * Tell the server our own run of a replay disagreed with theirs (S4-4).
 *
 * Fire and forget from the caller's point of view: the player is already being
 * shown that the replay is not trustworthy, and a failure to report it should
 * not turn into a second error message about the first one.
 */
export function reportDesync(
  token: string,
  battleId: string,
  body: DesyncReport
): Promise<{ ok: boolean }> {
  return request(`/api/v1/battles/${battleId}/desync`, z.object({ ok: z.boolean() }), {
    method: "POST",
    token,
    body,
  });
}

/**
 * Client events (S6-2, S5-4).
 *
 * Batched and fire-and-forget at the call site: a telemetry failure must never
 * be able to stop a player doing what they were doing.
 */
export function sendTelemetry(
  token: string,
  events: { name: string; props?: Record<string, unknown>; ts?: string }[]
): Promise<{ ok: boolean }> {
  return request("/api/v1/telemetry", z.object({ ok: z.boolean() }).passthrough(), {
    method: "POST",
    token,
    body: { events },
  });
}

/**
 * A fresh id for one attempt to launch.
 *
 * Generated when the player opens the confirm sheet, NOT when they tap send —
 * so a tap that times out and is tapped again reuses the same key and the
 * server returns the battle it already resolved. Regenerating per tap would
 * defeat the entire mechanism.
 */
export function newIdempotencyKey(): string {
  const rand =
    globalThis.crypto?.randomUUID?.() ??
    `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return `atk-${rand}`.slice(0, 100);
}
